import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useChipSelectionState } from './useChipSelectionState';
import type { SelectableChipProps, SelectableChipRenderProps } from './SelectableChip';
import { ChipSelectionClickHandler, useChipSelectionInteractions } from './useChipSelectionInteractions';

export type ToggleChipRenderProps = SelectableChipRenderProps & {
    isPopupOpen: boolean;
    /** Closes the popup while keeping the chip selected (e.g. after committing a value). */
    closePopup: () => void;
};

export type ToggleChipProps = Omit<SelectableChipProps, 'children'> & {
    children: (renderProps: ToggleChipRenderProps) => React.ReactNode;
};

/**
 * Behaviour wrapper for chips whose popup opens when the chip becomes selected
 * and then toggles open/closed on each click, staying selected throughout (e.g.
 * the due-date & assignment chips).
 *
 * Popup visibility is tracked separately from selection so a repeat click can
 * close the popup without deselecting. It's opened whenever the chip *becomes*
 * selected (by any path, not just a direct click) and closed when it's no longer
 * selected, so the shared dismissal paths (outside click, Escape, another chip)
 * hide it too.
 */
const ToggleChip: React.FC<ToggleChipProps> = ({ chip, canModify, onDelete, children }) => {
    const { isSelected, canSelect, isEditing, select, deselect, setEditing } = useChipSelectionState({
        chip,
        canModify,
    });

    const [isPopupOpen, setIsPopupOpen] = useState(isSelected);

    // Open the popup as the chip becomes selected, close it as it's deselected.
    // Keyed on the selection transition (not the click) so any selection path opens it.
    const wasSelectedRef = useRef(isSelected);
    useEffect(() => {
        if (isSelected === wasSelectedRef.current) return;
        wasSelectedRef.current = isSelected;
        setIsPopupOpen(isSelected);
    }, [isSelected]);

    // Closes the popup without deselecting (used by popup content after it commits).
    const closePopup = useCallback(() => setIsPopupOpen(false), []);

    const handleClick: ChipSelectionClickHandler = (event, focusChip) => {
        if (isSelected) {
            // Already selected: toggle only the popup, keeping the chip selected.
            event.stopPropagation();
            setIsPopupOpen((isOpen) => !isOpen);
            return;
        }
        if (!canSelect) return;

        // The selection transition (above) opens the popup.
        select();
        focusChip();
    };

    const { chipRef, chipProps } = useChipSelectionInteractions({
        isSelected,
        canActivate: canSelect,
        onClick: handleClick,
        onDeselect: deselect,
        onDelete: canModify ? onDelete : undefined,
        onEditingToggle: () => setEditing(!isEditing),
    });

    return (
        <>
            {children({
                isSelected,
                isPopupOpen,
                closePopup,
                canActivate: canSelect,
                isEditing,
                setEditing,
                deselect,
                chipRef,
                chipProps,
            })}
        </>
    );
};

export default ToggleChip;
